import {
	AfterViewInit,
	Component,
	EventEmitter,
	inject,
	input,
	Input,
	OnDestroy,
	OnInit,
	output,
	Output,
	ViewChild
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { CommonService } from "@modules/dashboard/services/common.service";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import {
	SelectOption,
	TransactionOptionsGroup,
	TransactionOption
} from "src/app/shared/models/select-option.interface";
import { PayoutProcessDialogComponent } from "../../../_components/payouts/payout-process/payout-process-dialog/payout-process-dialog/payout-process-dialog.component";
import { ManualPayoutProcessDialogComponent } from "@shared/components/manual-payout-process-dialog/manual-payout-process-dialog.component";
import { MultiLayerStatusFilterComponent } from "@shared/components/multi-layer-status-filter/multi-layer-status-filter.component";

@Component({
	selector: "app-tpp-process-payments",
	templateUrl: "./tpp-process-payments.component.html",
	styleUrls: ["./tpp-process-payments.component.scss"]
})
export class TppProcessPaymentsComponent implements OnInit, OnDestroy, AfterViewInit {
	@Output() processBookingClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() reprocessBookingClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() cancelBookingClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() completePaymentsClicked: EventEmitter<void> = new EventEmitter<void>();
	@Output() filterByStatus: EventEmitter<string[]> = new EventEmitter<string[]>();
	@Output() runChanged: EventEmitter<string> = new EventEmitter<string>();
	@Output() refresh: EventEmitter<void> = new EventEmitter<void>();

	resetPayoutIdList = output<string[]>();
	bulkInactivation = output<string[]>();
	bulkTransactionIds = input<string[]>();

	destroy$ = new Subject<void>();

	bookingsHasBeeenProcessed!: boolean;

	@Input() set _bookingsHasBeeenProcessed(hasBeenProcessed: boolean) {
		this.bookingsHasBeeenProcessed = hasBeenProcessed;
	}

	@Input() defualtStatusFilterValue: string[] = [];

	form!: FormGroup;

	payoutIdList!: string[];

	@Input() set _payoutIdList(list: string[]) {
		this.payoutIdList = list;
	}

	buttonState: string = "inactive";
	@Input() set _buttonState(state: string) {
		this.buttonState = state;
	}

	runSelection!: SelectOption[];
	fixedContainer = false;

	@Input() set _runSelection(selection: SelectOption[]) {
		this.runSelection = selection;
	}

	@Input() set fixedContainerStyle(fixed: boolean) {
		this.fixedContainer = fixed;
	}

	@ViewChild(MultiLayerStatusFilterComponent) multiLayerFilter!: MultiLayerStatusFilterComponent;

	canProcessBooking = false;
	allSelectableValues: string[] = [];
	statusOptions: SelectOption[] = [];
	transactionOptions: TransactionOptionsGroup[] = [];
	bulkSelected: TransactionOption = {
		text: "",
		value: "",
		group: "",
		selected: false,
		disable: false,
		statusesLinked: [],
		buttonText: ""
	};
	isBulkSelected = false;

	/** IMPORT DECLARATIONS */
	private dialog = inject(MatDialog);
	private formBuilder = inject(FormBuilder);
	private permissions = inject(PermissionsService);
	private commonService = inject(CommonService);

	ngOnInit(): void {
		this.statusOptions = this.commonService.getStatusOptions();
		this.transactionOptions = this.commonService.getTransactionOptions();
		this.allSelectableValues = this.commonService.getAllSelectableValues();

		this.permissions
			.canTriggerBookings()
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				this.canProcessBooking = res;
				this.setupRunForm();
			});
	}

	ngAfterViewInit(): void {
		this.syncInitialStatusFilterSelection();
	}

	setupRunForm() {
		this.form = this.formBuilder.group({
			selectedRun: [this.runSelection[0].value],
			statusPayment: [this.defualtStatusFilterValue],
			transactionStatuses: [this.transactionOptions]
		});

		this.form
			.get("selectedRun")
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(milestoneId => {
				this.runChanged.emit(milestoneId);
			});
	}

	selectionChange(): void {
		this.commonService.storeSelectionChange(
			this.form.get("statusPayment")?.value ? this.form.get("statusPayment")?.value : []
		);
	}

	/** Update payout list when status selection change */
	filterStatusSelectionChange(statuses: string[]): void {
		this.commonService.storeSelectionChange(statuses);
	}

	selectAllStatusses(): void {
		this.form.get("statusPayment")?.patchValue(this.allSelectableValues);
		this.selectionChange();
	}

	openDialog(text: string): void {
		let dialogRefData;

		dialogRefData = {
			processPaymentMenuSelection: text,
			isTPP: true
		};

		const dialogRef = this.dialog.open(PayoutProcessDialogComponent, {
			width: "666px",
			height: "441px",
			panelClass: "dialog-container",
			data: dialogRefData
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				if (text === "book the payments") {
					this.processBookingClicked.emit();
				} else {
					this.reprocessBookingClicked.emit();
					this.multiLayerFilter.resetStatusFilter(true);
					this.multiLayerFilter.updateOptionSelectedToDisplay();
				}
			}
		});
	}

	refreshPayouts() {
		this.refresh.emit();
	}

	/**
	 * Triggers the bulk action call
	 * @param bulkAction: bulk action selected value
	 */
	bulkActionButton(bulkAction: TransactionOption): void {
		switch (bulkAction.value) {
			case "INACTIVATE":
				this.multiLayerFilter.resetStatusFilter(true);
				this.multiLayerFilter.updateOptionSelectedToDisplay();
				this.bulkInactivation.emit(this.payoutIdList);
				break;
			case "MARK_AS_BOOKED":
			case "MARK_AS_PROCESSED":
				const status = bulkAction.value === "MARK_AS_BOOKED" ? "BOOKED" : "PROCESSED";
				this.markManual(bulkAction.buttonText!, status);
				break;
			default:
				break;
		}
	}

	/**
	 * Opens dialog box for Mark as booked/processed
	 * @param header: dialog box header text
	 */
	markManual(header: string, status: string): void {
		let dialogRefData;

		dialogRefData = {
			headerTitle: header,
			status: status,
			selectedTransactions: this.bulkTransactionIds()
		};

		const dialogRef = this.dialog.open(ManualPayoutProcessDialogComponent, {
			width: "750px",
			height: "550px",
			panelClass: "dialog-container",
			data: dialogRefData
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				this.resetPayoutIdList.emit([]);
				this.multiLayerFilter.resetStatusFilter(true);
				this.multiLayerFilter.updateOptionSelectedToDisplay();
				this.refreshPayouts();
			}
		});
	}

	/**
	 * Set status selection of bulk action
	 * @param bulkAction: bulk action selected object value
	 */
	setBulkActionSelected(bulkAction: TransactionOption): void {
		this.isBulkSelected = bulkAction.group === "bulkOption";
		this.bulkSelected = bulkAction;
		this.commonService.isBulkSelected(bulkAction);
	}

	private syncInitialStatusFilterSelection(): void {
		if (!this.multiLayerFilter || !this.defualtStatusFilterValue?.length) {
			return;
		}

		const statusGroup = this.multiLayerFilter.filteredTransactionOptions.find(group => group.type === "status");
		if (!statusGroup) {
			return;
		}

		const selectedValues = new Set(this.defualtStatusFilterValue);
		statusGroup.options.forEach(option => {
			if (option.value === "statuses") {
				option.selected = false;
				return;
			}

			option.selected = selectedValues.has(option.value);
		});

		this.multiLayerFilter.updateSelectedTransactionOptions();
		this.multiLayerFilter.filterStatusSelectionChange.emit(this.multiLayerFilter.selectedStatuses);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
